<template>
  <div :style="`fontFamily: ${store.state.theme.fontFamily}`">
    <v-container grid-list-xl fluid>
      <div class="function-strip">
        <functional-action
          style="display: inline-block"
          :loan="loan"
          :slaveLoanId="slaveLoanId"
          :privileges="authorization.privileges"
        ></functional-action>
        <activity-reminder :loan="loan" :restricts="authorization.restricts"></activity-reminder>
        <customer-local-time></customer-local-time>
        <template v-if = "followUpInfo.followUpFlag && isMatchRestricts(authorization.restricts, loan.loanStatus)" >
          <follow-up-reminder :loan="loan" :followUpInfo = "followUpInfo.followUpData"></follow-up-reminder>
        </template>
        <template v-if="matchUserPrivilege(authorization.privileges, dataType.PrivilegeCode.HANDLED_BY_RESOLUTIONS_DEPARTMENT, dataType.PrivilegeType.EXECUTABLE)" >
          <resolution-action :loan="loan"></resolution-action>
        </template>
        <template v-if="matchUserPrivilege(authorization.privileges, dataType.PrivilegeCode.UNDER_CONSULTATION_BUTTON, dataType.PrivilegeType.EXECUTABLE)&& loan.loanStatus === dataType.LoanStatus.UNDERWRITER_REVIEW.value" >
          <under-consultation :loan="loan"></under-consultation>
        </template>
        <template>
          <military-lending-act :loan="loan" :operator="operator"></military-lending-act>
        </template>
        <template v-if="matchUserPrivilege(authorization.privileges, dataType.PrivilegeCode.SPECIAL_CONSIDERATION_REASON, dataType.PrivilegeType.EXECUTABLE)">
          <special-consideration-note :loan="loan" :operator="operator"></special-consideration-note>
        </template>
        <template>
          <suspected-fraud :loan="loan" :authorization="authorization"></suspected-fraud>
        </template>
        <template>
          <instant-litigant-verification :loan="loan" :authorization="authorization" :operator="operator"></instant-litigant-verification>
        </template>
      </div>
      <v-card class="secondary detail-summary" ref="detailSummary">
        <v-card-text class="p-15">
          <v-layout row class="m-auto">
            <v-flex lg8 class="p-0">
              <application-detail-profile-summary
                :loan="loan"
                :operator="operator"
                :privileges="authorization.privileges"
                :restricts="authorization.restricts"
              ></application-detail-profile-summary>
            </v-flex>
            <v-flex lg3 class="p-0">
              <indicator-panel
                v-if="isShowElement(authorization.privileges, dataType.PrivilegeCode.LOAN_INDICATORS, dataType.PrivilegeType.EXECUTABLE)"
                :loan="loan">
              </indicator-panel>
            </v-flex>
            <v-flex lg1 class="p-0">
              <application-detail-quality-evaluation
                v-if="isShowElement(authorization.privileges, dataType.PrivilegeCode.LOAN_QUALITY, dataType.PrivilegeType.WRITABLE)"
                :loan="loan"
              >
              </application-detail-quality-evaluation>
            </v-flex>
          </v-layout>
        </v-card-text>
      </v-card>

      <v-card>
        <v-card-text>
          <v-layout row>
            <v-flex lg6 id="appDetailLeft" ref="appDetailLeft">
              <div class="application-detail-section">
                <old-profile-section
                  :layoutSetting="basicLayout.PERSONAL"
                  :loan="loan"
                  :authorization="authorization"
                  :operator="operator"
                  @calcHeight="calcHeight"
                  :toolTipData="toolTipData"
                  :stamp="stamp.personal"
                  :blackList="blackList"
                  :warningList="warningList"
                  :personalNameInBlackList="personalNameInBlackList"
                  @updatePersonalName="updatePersonalName"
                ></old-profile-section>
              </div>

              <div class="application-detail-section">
                <old-profile-section
                  :layoutSetting="basicLayout.EMPLOYMENT"
                  :loan="loan"
                  :authorization="authorization"
                  :operator="operator"
                  @calcHeight="calcHeight"
                  :stamp="stamp.employment"
                  :warningList="warningList"
                  :employmentNameInBlackList="employmentNameInBlackList"
                  @updateEmploymentName="updateEmploymentName"
                ></old-profile-section>
              </div>

              <div class="application-detail-section">
                <old-profile-section
                  :layoutSetting="basicLayout.BANK"
                  :loan="loan"
                  :authorization="authorization"
                  :operator="operator"
                  @calcHeight="calcHeight"
                  :stamp="stamp.banking"
                  :blackList="blackList"
                ></old-profile-section>
              </div>

              <div class="application-detail-section">
                <old-profile-section
                  :layoutSetting="basicLayout.BANK_RECORDS"
                  :loan="loan"
                  :warningList="warningList"
                  :authorization="authorization"
                  @calcHeight="calcHeight"
                ></old-profile-section>
              </div>
            </v-flex>

            <v-flex lg6 id="appDetailRight" ref="appDetailRight">
              <div class="application-detail-section"
                   id="appLoanDetails"
                   ref="appLoanDetails">
                <old-profile-section
                  :layoutSetting="layoutSetting.LOAN_DETAILS"
                  :loan="loan"
                  :authorization="authorization"
                  :toolTipData="toolTipData"
                  :slaveLoanId="slaveLoanId"
                  :stamp="stamp.paymentOption"
                ></old-profile-section>
              </div>

              <div
                v-if="loan.loanType === dataType.LoanTypeEnum.CREDIT_LINE_LOAN.value"
                class="application-detail-section"
                id="appDrawHistory"
                ref="appDrawHistory"
              >
                <draw-history-table
                  :layoutSetting="layoutSetting.DRAW_HISTORY"
                  :loan="loan"
                ></draw-history-table>
              </div>

              <div class="application-detail-section"
                   id="appPaymentSchedule"
                   ref="appPaymentSchedule">
                <loan-detail-payment-schedule
                  :layoutSetting="layoutSetting.PAYMENT_SCHEDULE"
                  :loan="loan"
                  :height="sectionHeight['payment-schedule']"
                  :installmentData="installmentData"
                  :toolTipData="toolTipData"
                  :slaveLoanId="slaveLoanId"
                ></loan-detail-payment-schedule>
              </div>

              <div class="application-detail-last-section secondary"
                   id="appPaymentProgress"
                   ref="appPaymentProgress"
              >
                <v-layout row>
                  <v-flex lg7 class="m-l-15">
                    <loan-detail-payment-progress
                      :loan="loan"
                      :installmentData="installmentData"
                      :slaveLoanId="slaveLoanId"
                      :layoutSetting="basicLayout.PAYMENT_PROGRESS"
                    ></loan-detail-payment-progress>
                  </v-flex>
                  <v-flex lg4>
                    <document-folder
                      :loan="loan"
                    ></document-folder>
                  </v-flex>
                </v-layout>
              </div>
            </v-flex>
          </v-layout>

          <v-layout row>
            <v-flex lg12>
              <tribe-summary></tribe-summary>
            </v-flex>
          </v-layout>
        </v-card-text>
      </v-card>

      <status-action
        :loan="loan"
        :authorization="authorization"
      ></status-action>
    </v-container>

    <right-panel :loan="loan" :slaveLoan="slaveLoan" :authorization="authorization"></right-panel>
    <v-snackbar
      v-model="resetFollowUp.snackbar"
      :timeout="resetFollowUp.timeout"
      :right=true
      :top=true
    >
      <div class="snackbar">
        <div>
          <v-toolbar-title class="body-2">
            <span class="headline snackbar-title">Reset Follow Up</span>
          </v-toolbar-title>
          <span class="snackbar-text">
          {{ resetFollowUp.text }}
        </span>
        </div>
        <div style="width: 100%">
          <v-btn
            class="snackbar-button"
            color="blue"
            text
            @click="closeFollowUpReset()"
          >
            OK
          </v-btn>
        </div>
      </div>
    </v-snackbar>
  </div>
</template>

<script>
import { BASIC_DETAIL_LAYOUT_SETTING } from '@/setting/layout/basic/detail'
import { LOS_DETAIL_LAYOUT_SETTING } from '@/setting/layout/pre-origination/detail'
import { Authentication, Crypto, DataType, Tool, Popup, Cache, LockSocket } from '@/util/index'
import { LoanOperationApi, OriginationApi, OriginationBusinessApi, RuntimeApi } from '@/api/pandaria'
import { _ } from 'vue-underscore'
import FunctionalAction from '@/components/panda/pre-origination/detail/FunctionalAction'
import ActivityReminder from '@/components/panda/pre-origination/detail/ActivityReminder'
import DocumentFolder from '@/components/panda/pre-origination/detail/DocumentFolder'
import DrawHistoryTable from '@/components/panda/pre-origination/detail/DrawHistoryTable'
import StatusAction from '@/components/panda/pre-origination/detail/StatusAction'
import OldProfileSection from '@/components/panda/common/detail/OldProfileSection'
import CustomerLocalTime from '@/components/panda/pre-origination/detail/CustomerLocalTime'
import FollowUpReminder from '@/components/panda/pre-origination/detail/FollowUpReminder'
import ResolutionAction from '@/components/panda/pre-origination/detail/ResolutionAction'
import UnderConsultation from '@/components/panda/pre-origination/detail/UnderConsultation'
import MilitaryLendingAct from '@/components/panda/pre-origination/detail/MilitaryLendingAct'
import SpecialConsiderationNote from '@/components/panda/pre-origination/detail/SpecialConsiderationNote'
import SuspectedFraud from '@/components/panda/pre-origination/detail/SuspectedFraud'
import InstantLitigantVerification from '@/components/panda/pre-origination/detail/InstantLitigantVerification'
import { ComplaintApi } from '@/api/lms/complaint'
import { SystemEnum } from '@/assets/js'

const tool = new Tool()
const popup = new Popup()
const cache = new Cache()
export default {
  inject: ['reload'],
  components: {
    ResolutionAction,
    FollowUpReminder,
    CustomerLocalTime,
    OldProfileSection,
    FunctionalAction,
    ActivityReminder,
    DocumentFolder,
    DrawHistoryTable,
    StatusAction,
    UnderConsultation,
    MilitaryLendingAct,
    SpecialConsiderationNote,
    SuspectedFraud,
    InstantLitigantVerification
  },
  data () {
    return {
      basicLayout: BASIC_DETAIL_LAYOUT_SETTING,
      layoutSetting: LOS_DETAIL_LAYOUT_SETTING,
      loan: {},
      stamp: {
        personal: [],
        employment: [],
        banking: [],
        paymentOption: []
      },
      operator: {},
      sectionHeight: {
        personal: null,
        employment: null,
        statement: null,
        bank: null,
        'payment-option': null,
        'draw-history': null,
        'payment-schedule': 400,
        'payment-progress': null,
        appLeft: null,
        appRight: null
      },
      dataType: DataType,
      installmentData: {
        type: Object,
        default: () => {}
      },
      authorization: {
        privileges: [],
        restricts: []
      },
      currentLockedId: -1,
      toolTipData: '',
      originalSpeechCraft: [],
      currentSpeechCraft: [],
      slaveLoanId: null,
      slaveLoan: {},
      followUpInfo: {
        followUpFlag: false,
        followUpData: {}
      },
      resetFollowUp: {
        snackbar: false,
        timeout: 2000000,
        text: 'The loan\'s follow up will be reset',
        time: 20,
        acceptText: 'Reset',
        timer: null
      },
      blackList: {
        ssn: false,
        email: false,
        state: false,
        accountNo: false,
        routingNo: false,
        zip: false,
        birthday: false
      },
      warningList: {
        zip: false,
        email: false,
        lastPayday: false
      },
      personalNameInBlackList: false,
      employmentNameInBlackList: false,
      unlockReloadFlag: true,
      taskTime: new Date().getTime(),
      initSpecialConsideration: {}
    }
  },
  computed: {
    encryptLoanId () {
      return cache.getLoanId()
    },
    loanId () {
      return parseInt(Crypto.decrypt(cache.getSessionLoanId()), 10)
    }
  },
  watch: {
    encryptLoanId: function (newVal, oldValue) {
      if (newVal !== oldValue) {
        this.reload()
        this.initApplication()
        this.initSpeechCraft()
      }
    },
    loan (val) {
      const $this = this
      const portfolioId = $this.loan.portfolioId
      RuntimeApi.fetchPortfolioParameter(portfolioId, function (portfolio) {
        $this.store.commit('setPortfolioJson', portfolio)
      })
      this.isHasFollowUp()
    },
    'store.getters.getAccount': {
      handler: function () {
        this.operator = this.store.getters.getAccount
        this.$forceUpdate()
      }
    },
    'store.getters.getAuthorization': {
      handler: function () {
        this.authorization = this.store.getters.getAuthorization
        this.$forceUpdate()
      }
    }
  },
  destroyed () {
    this.$eventBus.$off('reLockLoan')
    this.$eventBus.$off('reloadLoan')
    this.$eventBus.$off('calculateLoanSize')
    this.$eventBus.$off('updateSignStatus')
    this.$eventBus.$off('broadcastLockedId')
    this.$eventBus.$off('showTooltips')
    this.$eventBus.$off('updateStatus')
    this.$eventBus.$off('getSpeechCraftTemplate')
    this.$eventBus.$off('loanIntegratedComputationResult')
    this.$eventBus.$off('resetFollowUp')
    this.$eventBus.$off('updateFollowUpData')
    this.$eventBus.$off('refreshVerifyStamp')
    this.stopInterval()
    this.closeWebSocket()
    if (tool.isNotEmpty(this.loan.id) && this.unlockReloadFlag) {
      LoanOperationApi.unlockLoan({ loanId: this.loan.id, unlockReason: DataType.UnlockLoanReasonEnum.AUTO_UNLOCK_LEAVE_PAGE.value })
    }
  },
  created () {
    if (tool.isNotEmpty(this.loanId)) {
      this.initApplication()
      this.initSpeechCraft()
      this.initWebSocket()
      this.loanComplaint()
    }
  },
  mounted () {
    this.$eventBus.$on('loanIdCheck', target => {
      this.initApplication()
      this.initSpeechCraft()
      this.initWebSocket()
      this.loanComplaint()
    })
    this.$eventBus.$on('updateFollowUpData', target => {
      this.isHasFollowUp()
    })
    this.$eventBus.$on('reLockLoan', target => {
      this.stopInterval()
      this.closeWebSocket()
      this.initWebSocket()
      this.startInterval()
    })
    this.$eventBus.$on('reloadLoan', target => {
      this.unlockReloadFlag = false
      this.reload()
    })
    this.$eventBus.$on('calculateLoanSize', target => {
      const calculateRequest = {
        loanId: this.loan.id,
        calculateLoanAmount: true
      }
      this.loanIntegratedComputation(calculateRequest)
    })
    this.$eventBus.$on('changeSignStatus', target => {
      if (this.loanId === target.loanId) {
        this.loan.signStatus = target.signStatus
        this.$eventBus.$emit('updateProfileSectionSignStatus', target.signStatus)
        if (target.signStatus === DataType.SignStatusEnum.SIGNED.value &&
          tool.isNotEmpty(target.loanStatus) && target.loanStatus === DataType.LoanStatus.UNDERWRITER_REVIEW.value
        ) {
          this.$eventBus.$emit('forceUnlock', { unlockReason: DataType.UnlockLoanReasonEnum.STATUS_CHANGED.value })
          Object.assign(this.loan, { loanStatus: target.loanStatus, loanStatusText: DataType.LoanStatus.UNDERWRITER_REVIEW.text })
        }
      }
    })
    this.$eventBus.$on('updateSignStatus', target => {
      this.loan.signStatus = target.value
      this.$eventBus.$emit('updateProfileSectionSignStatus', target.value)
    })
    this.$eventBus.$on('refreshVerifyStamp', target => {
      this.listLoanStampData()
    })
    this.$eventBus.$on('broadcastLockedId', target => {
      this.currentLockedId = target
      this.$forceUpdate()
    })
    this.$eventBus.$on('showTooltips', target => {
      this.currentSpeechCraft = JSON.parse(JSON.stringify(this.originalSpeechCraft))
      const $this = this
      const loanData = this.store.getters.getLoan
      loanData.cardNo = this.store.getters.getCardNo
      if (tool.isNotEmpty(loanData)) {
        const language = loanData.language
        let languageCode = this.getLanguageCodeByValue(language)
        if (tool.isEmpty(languageCode)) {
          languageCode = 'GB'
        }
        const loanType = loanData.loanType
        const loanFlag = loanData.flags
        if ((loanFlag & DataType.LoanFlagsEnum.TRANSFERRED.value) === DataType.LoanFlagsEnum.TRANSFERRED.value && target === 'FOLLOWED_ACH_DISCLOSURE') {
          target = 'LT_' + target
        } else if (loanType === DataType.LoanTypeEnum.INSTALLMENT_LOAN.value) {
          target = 'RNL_' + target
        }
        let hasData = false
        let templateData = ''
        for (const speechCraftItem of $this.currentSpeechCraft) {
          if (speechCraftItem.code === target && speechCraftItem.languageCode === languageCode) {
            hasData = true
            templateData = speechCraftItem.template
          }
        }
        if (hasData && tool.isNotEmpty(templateData)) {
          const data = tool.getSpeechCraftData(loanData, languageCode)
          $this.toolTipData = _.template(templateData)({ data: data })
        } else {
          $this.toolTipData = ''
        }
      }
    })
    this.$eventBus.$on('getSpeechCraftTemplate', target => {
      this.currentSpeechCraft = JSON.parse(JSON.stringify(this.originalSpeechCraft))
      const loanData = this.store.getters.getLoan
      if (tool.isNotEmpty(loanData)) {
        const language = loanData.language
        let languageCode = this.getLanguageCodeByValue(language)
        if (tool.isEmpty(languageCode)) {
          languageCode = 'GB'
        }

        const speechArray = []
        for (const item of this.currentSpeechCraft) {
          if (loanData.loanType === this.dataType.LoanTypeEnum.MULTIPLE_ADVANCE_LOAN.value) {
            if (item.code.indexOf('ELL') !== -1 && item.languageCode === languageCode) {
              speechArray.push(item)
            }
          } else {
            if (item.code.indexOf('ELL') === -1 && item.languageCode === languageCode) {
              speechArray.push(item)
            }
          }
        }
        for (const speechCraftItem of speechArray) {
          if (speechCraftItem.languageCode === languageCode) {
            const data = tool.getSpeechCraftData(loanData, languageCode)
            speechCraftItem.template = _.template(speechCraftItem.template)({ data: data })
          }
        }

        this.$eventBus.$emit('setSpeechCraftTemplate', speechArray)
      }
    })
    this.$eventBus.$on('updateStatus', target => {
      const $this = this
      const request = {
        loanId: $this.loanId,
        feature: 'STATUS'
      }
      OriginationApi.getLoanByIdAndFeature(request, function (result) {
        $this.store.commit('setLoan', { loan: result })
        Object.assign($this.loan, result)
      })
    })
    this.$eventBus.$on('loanIntegratedComputationResult', target => {
      this.loanIntegratedComputationResult(target)
    })
    this.$eventBus.$on('resetFollowUp', target => {
      if (this.followUpInfo.followUpFlag) {
        this.resetFollowUp.snackbar = true
        if (this.resetFollowUp.time === 20) {
          this.resetFollowUp.timer = setInterval(() => this.resetTimeDown(), 1000)
        }
        this.resetFollowUpAction()
      }
    })

    const account = this.store.getters.getAccount
    if (tool.isNotEmpty(account)) {
      this.operator = account
    }
    const authorization = this.store.getters.getAuthorization
    if (tool.isNotEmpty(authorization)) {
      this.authorization = authorization
    }
    this.$eventBus.$on('showWarningListIcon', target => {
      if (target.section === SystemEnum.SECTION_FEATURE.BANK_RECORDS) {
        this.warningList.lastPayday = target.data.lastPayday
      }
    })
  },
  methods: {
    initWebSocket () {
      const $this = this
      const token = localStorage.getItem('Access-Token')
      const env = process.env.NODE_ENV
      $this.lockSocket = new LockSocket({
        env: env,
        loanId: $this.loanId,
        token: token
      })
      $this.lockSocket.init()
      $this.lockSocket.on('message', function (message) {
        $this.stopInterval()
        $this.closeWebSocket()
        $this.$eventBus.$emit('updateCurrentLockedId', -1)
      })
    },
    closeWebSocket () {
      this.lockSocket.close()
    },
    renewLock () {
      if (tool.isNotEmpty(this.loanId)) {
        LoanOperationApi.renewLock(this.loanId, localStorage.getItem('Access-Token'), function () {
        }, function () {
          this.renewLock()
        })
      }
    },
    updatePersonalName (data) {
      this.personalNameInBlackList = data
    },
    updateEmploymentName (data) {
      this.employmentNameInBlackList = data
    },
    getLanguageCodeByValue (language) {
      let code = ''

      const languageEnum = DataType.LanguageEnum
      for (const item in languageEnum) {
        if (languageEnum[item].value === language) {
          code = languageEnum[item].code
        }
      }

      return code
    },
    initSpeechCraft () {
      const $this = this
      RuntimeApi.fetchScriptTemplateByCompanyId(1, function (result) {
        if (tool.isNotEmpty(result)) {
          $this.originalSpeechCraft = result
        }
      })
    },
    isShowElement (privileges, code, value) {
      return Authentication.matchElementPrivilege(privileges, code, value) && Authentication.isLockedByCurrentUser(this.store.getters.getAccount, this.currentLockedId)
    },
    isMatchRestricts (restricts, loanStatus) {
      return Authentication.matchRestricts(restricts, loanStatus) && Authentication.isLockedByCurrentUser(this.store.getters.getAccount, this.currentLockedId)
    },
    isShowScriptTemplate () {
      let result = false
      if (tool.isNotEmpty(this.loan) && tool.isNotEmpty(this.loan.loanType) && this.loan.loanType === DataType.LoanTypeEnum.MULTIPLE_ADVANCE_LOAN.value) {
        result = true
      }
      return result
    },
    matchUserPrivilege (privileges, code, value) {
      return Authentication.matchElementPrivilege(privileges, code, value)
    },
    calcHeight (height, feature) {
      this.sectionHeight[feature] = height
      this.calcScheduleHeight()
    },
    calcScheduleHeight () {
      if (tool.isNotEmpty(this.sectionHeight.personal) &&
        tool.isNotEmpty(this.sectionHeight.employment) &&
        tool.isNotEmpty(this.sectionHeight.bank) &&
        tool.isNotEmpty(this.sectionHeight.statement)) {
        this.sectionHeight.appLeft = this.$refs.appDetailLeft.offsetHeight
        this.sectionHeight.appRight = this.$refs.appDetailRight.offsetHeight
        this.sectionHeight['payment-option'] = this.$refs.appLoanDetails.offsetHeight
        this.sectionHeight['draw-history'] = tool.isNull(this.$refs.appDrawHistory) ? 0 : this.$refs.appDrawHistory.offsetHeight
        this.sectionHeight['payment-progress'] = this.$refs.appPaymentProgress.offsetHeight
        this.sectionHeight['payment-schedule'] =
          this.sectionHeight.appLeft -
          this.sectionHeight['payment-option'] -
          this.sectionHeight['draw-history'] -
          this.sectionHeight['payment-progress']
        this.$forceUpdate()
      }
    },
    initApplication () {
      const $this = this
      const request = {
        loanId: this.loanId,
        feature: 'STATUS,SIGN,QUALITY,IBV',
        isQueryIbvData: false
      }
      OriginationApi.getLoanByIdAndFeature(
        request,
        function (result) {
          result.loanType = Number.parseInt(result.loanType)
          $this.store.commit('clearLoan')
          $this.store.commit('clearPaymentSchedule')
          $this.store.commit('setPortfolioId', result.portfolioId)
          if (result.loanType === DataType.LoanTypeEnum.MULTIPLE_ADVANCE_LOAN.value) {
            OriginationBusinessApi.initSlaveLoan({ parentId: $this.loanId }, function (res) {
              if (tool.isNotEmpty(res)) {
                $this.slaveLoanId = res.id
                $this.slaveLoan = res
              }
            })
          }
          $this.store.commit('setLoan', { loan: result })
          $this.loan = result
        },
        function (result) {
          console.log(result.message)
        })
      $this.listLoanStampData()
      $this.listLoanErrorData()
      $this.listLoanWarningData()
      $this.renewLock()
      $this.startInterval()
    },
    startInterval () {
      const $this = this
      $this.renewTask = setInterval(function () {
        $this.renewLock()
      }, 40 * 1000)
    },
    stopInterval () {
      clearInterval(this.renewTask)
      this.renewTask = null
    },
    loanComplaint () {
      const $this = this
      ComplaintApi.loanComplaint({ loanId: this.loanId }, function (result) {
        $this.$eventBus.$emit('complaintStatus', result)
      })
    },
    // 获取 list error data通过 loan_id
    listLoanErrorData () {
      if (tool.isEmpty(this.loanId)) {
        return
      }
      const $this = this
      const request = {
        loanId: this.loanId,
        feature: 'BLACK_LIST'
      }
      OriginationApi.listSectionFeature(request, function (callbackData) {
        if (tool.isNotEmpty(callbackData)) {
          for (const item of callbackData) {
            const fieldKey = item.fieldKey
            if (fieldKey === DataType.BlacklistEnum.EMAIL) {
              $this.blackList.email = true
            }
            if (fieldKey === DataType.BlacklistEnum.SSN) {
              $this.blackList.ssn = true
            }
            if (fieldKey === DataType.BlacklistEnum.STATE) {
              $this.blackList.state = true
            }
            if (fieldKey === DataType.BlacklistEnum.ACCOUNT_NUMBER) {
              $this.blackList.accountNo = true
            }
            if (fieldKey === DataType.BlacklistEnum.ROUTING_NUMBER) {
              $this.blackList.routingNo = true
            }
            if (fieldKey === DataType.BlacklistEnum.PERSONAL_NAME) {
              $this.personalNameInBlackList = true
            }
            if (fieldKey === DataType.BlacklistEnum.EMPLOYMENT_NAME) {
              $this.employmentNameInBlackList = true
            }
            if (fieldKey === DataType.BlacklistEnum.ZIP) {
              $this.blackList.zip = true
            }
            if (fieldKey === DataType.BlacklistEnum.BIRTHDAY) {
              $this.blackList.birthday = true
            }
          }
        }
      })
    },
    listLoanStampData () {
      if (tool.isEmpty(this.loanId)) {
        return
      }
      const $this = this
      const payload = {
        loanId: this.loanId,
        feature: 'STAMP',
        condition: {
          sectionKey: null
        }
      }
      this.stamp = {
        personal: [],
        employment: [],
        banking: [],
        paymentOption: []
      }
      OriginationApi.listSectionFeature(payload, function (callbackData) {
        if (tool.isNotEmpty(callbackData)) {
          const keys = Object.keys($this.stamp)
          for (const item of callbackData) {
            const sectionKey = item.sectionKey
            if (keys.includes(sectionKey)) {
              $this.stamp[sectionKey].push(item)
            }
          }
        }
      })
    },
    listLoanWarningData () {
      if (tool.isEmpty(this.loanId)) {
        return
      }
      const $this = this
      const request = {
        loanId: this.loanId,
        feature: 'WARNING'
      }
      OriginationApi.listSectionFeature(request, function (result) {
        if (tool.isNotEmpty(result)) {
          for (const item of result) {
            const filedKey = item.fieldKey
            if (filedKey === DataType.WarningListEnum.ZIP) {
              $this.warningList.zip = true
            } else if (filedKey === DataType.WarningListEnum.EMAIL) {
              $this.warningList.email = true
            } else if (filedKey === DataType.WarningListEnum.LAST_PAY_DAY) {
              $this.warningList.lastPayday = true
            }
          }
        }
      })
    },
    loanIntegratedComputationResult (result) {
      if (tool.isNotEmpty(result)) {
        const $this = this
        const loanAmountResult = result.loanAmountResult
        const paymentScheduleResult = result.paymentScheduleResult
        const loanAgreementResult = result.loanAgreementResult
        const fraud = result.fraud
        if (tool.isNotEmpty(fraud)) {
          popup.popupForInfo(fraud)
        }
        if (DataType.CalculateResultFlag.SUCCESS.value === result.flag) {
          if (tool.isNotEmpty(loanAmountResult)) {
            $this.$eventBus.$emit('loanAmountUpdated', { loanAmount: loanAmountResult.loanAmount, approvedAmount: loanAmountResult.approvedAmount })
          }
          if (tool.isNotEmpty(paymentScheduleResult)) {
            const updateData = {
              debitCount: paymentScheduleResult.installmentList.length - 1,
              firstDebitDate: paymentScheduleResult.firstDebitDate,
              lastDebitDate: paymentScheduleResult.lastDebitDate,
              regularAmount: paymentScheduleResult.regularAmount,
              apr: paymentScheduleResult.apr
            }
            $this.installmentData = paymentScheduleResult
            $this.store.commit('setHasPaymentSchedule', true)
            $this.$eventBus.$emit('paymentScheduleUpdate', updateData)
            $this.store.commit('setLoan', { loan: updateData })
          }
        } else if (DataType.CalculateResultFlag.CLEAR_LOAN_AMOUNT.value === result.flag) {
          $this.installmentData = {}
          $this.$eventBus.$emit('clearData')
        } else if (DataType.CalculateResultFlag.CLEAR_PAYMENT_SCHEDULE.value === result.flag) {
          $this.installmentData = {}
          if (tool.isNotEmpty(loanAmountResult)) {
            $this.$eventBus.$emit('loanAmountUpdated', { loanAmount: loanAmountResult.loanAmount, approvedAmount: loanAmountResult.approvedAmount })
          }
          $this.$eventBus.$emit('clearScheduleData')
        } else if (DataType.LoanActionEnum.UPDATE_LOAN_FLAGS_JARVIS.value === result.flag) {
          $this.loan.flags = result.flags
          $this.store.commit('setLoan', { flags: result.flags })
        } else if (DataType.LoanActionEnum.UPDATE_LOAN_FLAGS_FOX_V4.value === result.flag) {
          $this.loan.FoxFlag = result.flag
          $this.loan.flags = result.flags
          $this.store.commit('setLoan', { flags: result.flags })
        }
        if (tool.isNotEmpty(loanAgreementResult)) {
          if (loanAgreementResult === 'void') {
            popup.popupForInfo('This loan agreement has been void')
            $this.$eventBus.$emit('updateSignStatus', DataType.SignStatusEnum.VOIDED)
          } else if (loanAgreementResult === 'suspend') {
            $this.$eventBus.$emit('updateSignStatus', DataType.SignStatusEnum.SUSPENDED)
          }
        }
      }
    },
    loanIntegratedComputation (calculateRequest) {
      const $this = this
      OriginationBusinessApi.loanIntegratedComputation(calculateRequest, function (result) {
        $this.loanIntegratedComputationResult(result)
      })
    },
    isHasFollowUp () {
      const $this = this
      OriginationApi.getFollowUpByLoanId($this.loan.id, function (result) {
        if (tool.isNotEmpty(result) && tool.isNotEmpty(result.followUpId)) {
          $this.followUpInfo.followUpData = result
          $this.followUpInfo.followUpFlag = true
        }
      })
      return $this.followUpInfo
    },
    resetTimeDown () {
      if (this.resetFollowUp.time - 1 < 0) {
        this.closeFollowUpReset()
      } else {
        this.resetFollowUp.time = this.resetFollowUp.time - 1
      }
    },
    closeFollowUpReset () {
      clearInterval(this.resetFollowUp.timer)
      this.resetFollowUp.time = 20
      this.resetFollowUp.snackbar = false
    },
    resetFollowUpAction () {
      const $this = this
      OriginationBusinessApi.followUpReset({ loanId: this.loanId }, function (result) {
        $this.followUpInfo.followUpData = null
        $this.followUpInfo.followUpFlag = false
      })
    }
  }
}
</script>

<style scoped>
.application-detail {
  width: 100%;
}

.function-strip {
  background-color: #FAFAFA;
  margin-top: 0;
  margin-bottom: 12px;
}

.detail-summary {
  min-height: 98px;
}
.v-card + .v-card {
  margin-top: 11px;
}

.application-detail-section + .application-detail-section {
  border-top: 1px solid #b9c3ca;
}

.application-detail-last-section {
  margin-top: 12px;
}
.snackbar {
  height: 60px!important;
  width: 320px!important;
}
.snackbar-title {
  font-size: 14px!important;
}
.snackbar-text {
  font-size: 10px
}
.snackbar-button {
  background-color: #e0e0e0;
  margin-top: -40px!important;
  font-size: 8px!important;float: right
}
</style>
